// API リクエストに付ける認証トークン(Cognito の ID トークン)の取得。plugins/axios.js の onRequest から使う。
//
// Amplify の Auth.currentSession() は、トークンの期限が切れていると裏でリフレッシュ通信を行う。
// その通信が一瞬失敗しただけでも reject されるため、以前はヘッダー無しで BE に送ってしまい、
// BE の 401 → 共通処理でサインアウト、という流れで利用者がログアウトされていた。
// ここでは 1 回だけ再試行し、それでも取れないときは「セッションが無い」のか「一時的に取れない」のかを区別する。
// 一時的に取れないときもリクエストはヘッダー無しで送る(認証不要の API は従来どおり成功する)が、
// request.tokenUnavailable を立てておき、onResponseError はその 401 ではサインアウトしない。

export const TOKEN_RETRY_DELAY_MS = 500
// 直前に「一時的に取れない」と判定してからこの時間内は再試行せず 1 回だけ試す
// (ページ遷移ごとに走るメンテナンス確認などが、失敗のたびに 500ms 待たされないようにする)
export const TOKEN_RETRY_COOLDOWN_MS = 5000
// 同じ種類の Sentry 通知を続けて送らない間隔(Cognito が不安定なときに利用者全員から大量に届かないように)
export const TOKEN_REPORT_COOLDOWN_MS = 60000

// 再試行しても解消しない失敗(ログインしていない / セッション情報が欠けている / リフレッシュトークンが無効 など)か。
// 通信失敗(amazon-cognito-identity-js の code 'NetworkError' / message 'Network error')は含めない。
//
// 判定に使うメッセージの出どころ
//   'No current user'                                          @aws-amplify/auth Auth.currentUserPoolUser (未ログイン。文字列で reject される)
//   'Local storage is missing an ID Token, Please authenticate' amazon-cognito-identity-js CognitoUser.getSession (ID トークンが無い)
//   'Cannot retrieve a new session. Please authenticate.'      同上 (期限切れでリフレッシュトークンが無い)
//   'User is not authenticated'                                同 CognitoUser (サインイン前の操作)
//   'Username is null. Cannot retrieve a new session'          同 CognitoUser.refreshSession
//   'Refresh Token has expired' / 'Refresh Token has been revoked' / 'Invalid Refresh Token'
//                                                              Cognito の NotAuthorizedException (リフレッシュ失敗)
export const isSessionLostError = (error) => {
  if (!error) {
    return false
  }
  const code = error.code || error.name || ''
  if (
    code === 'NotAuthorizedException' ||
    code === 'UserNotFoundException' ||
    code === 'PasswordResetRequiredException'
  ) {
    return true
  }
  const message = typeof error === 'string' ? error : error.message || ''
  return (
    /no current user/i.test(message) ||
    /please authenticate/i.test(message) ||
    /user is not authenticated/i.test(message) ||
    /cannot retrieve a new session/i.test(message) ||
    /refresh token/i.test(message)
  )
}

const defaultWait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// トークンを取得する。結果は次のいずれか
//   { status: 'ok', token, retried, error }     取得できた。retried は再試行の末に取れたか(その場合 error は 1 回目の失敗)
//   { status: 'session-lost', error }           セッションが無い(再試行しても解消しない)。ヘッダー無しで送る
//   { status: 'unavailable', error, retried }   一時的に取得できない(通信失敗など)。retried は再試行したうえで失敗したか
// retry=false のときは再試行せず 1 回だけ試す(retried=false になる)
export const resolveAuthToken = async ({
  getToken,
  retry = true,
  retryDelayMs = TOKEN_RETRY_DELAY_MS,
  wait = defaultWait
}) => {
  const attempts = retry ? 2 : 1
  let lastError
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const token = await getToken()
      if (token) {
        return attempt === 0
          ? { status: 'ok', token, retried: false }
          : { status: 'ok', token, retried: true, error: lastError }
      }
      // セッションはあるのにトークンが空。再試行で変わるものではないので、ヘッダー無しで送って BE の判断に任せる
      return { status: 'session-lost', error: new Error('empty token') }
    } catch (error) {
      lastError = error
      // Amplify は userPool 未設定のとき値なし(undefined)で reject する。再試行しても解消しないので session-lost 扱い
      if (error === undefined || error === null || isSessionLostError(error)) {
        return { status: 'session-lost', error }
      }
    }
    if (attempt < attempts - 1) {
      await wait(retryDelayMs)
    }
  }
  return { status: 'unavailable', error: lastError, retried: retry }
}

// resolveAuthToken を「同時に呼ばれたら 1 回の取得を共有し(単一飛行)、直前に unavailable だった直後は再試行しない」
// 形に包んだ関数を返す。ページ表示時に複数の API が同時に走ってもリフレッシュ通信は 1 系統で済む
export const createAuthTokenResolver = ({
  getToken,
  retryDelayMs = TOKEN_RETRY_DELAY_MS,
  cooldownMs = TOKEN_RETRY_COOLDOWN_MS,
  wait = defaultWait,
  now = () => Date.now()
}) => {
  let inFlight = null
  let lastUnavailableAt = -Infinity
  return () => {
    if (inFlight) {
      return inFlight
    }
    const retry = now() - lastUnavailableAt >= cooldownMs
    inFlight = resolveAuthToken({ getToken, retry, retryDelayMs, wait })
      .then((result) => {
        if (result.status === 'unavailable') {
          lastUnavailableAt = now()
        }
        return result
      })
      .finally(() => {
        inFlight = null
      })
    return inFlight
  }
}

export const TOKEN_UNAVAILABLE_MESSAGE =
  '通信に失敗しました。電波の良い場所で再度お試しください。'

// トークンが一時的に取れないまま送ったリクエストの 401 に印を付ける(トーストの文言と、サインアウトしない判定に使う)
export const markTokenUnavailable = (error) => {
  if (error && typeof error === 'object') {
    error.isTokenUnavailable = true
  }
  return error
}

// Sentry への通知。$sentry は @nuxtjs/sentry が注入するもので、無効化環境ではモックが入る。無い場合は何もしない
const captureTokenEvent = ({
  sentry,
  message,
  level,
  result,
  error,
  path,
  method,
  url
}) => {
  if (!sentry || typeof sentry.captureMessage !== 'function') {
    return false
  }
  const cause = error == null ? {} : error
  sentry.captureMessage(message, {
    level,
    tags: {
      feature: 'auth-token',
      result,
      cause_code: cause.code || cause.name || 'unknown'
    },
    extra: {
      cause_message:
        typeof error === 'string' ? error : cause.message || String(error),
      path,
      method,
      url
    }
  })
  return true
}

// 再試行しても取れなかった(ヘッダー無しで送った)ことを warning で送る。クールダウン内の 2 回目以降(retried=false)は送らない
export const reportTokenUnavailable = ({ sentry, error, path, method, url }) =>
  captureTokenEvent({
    sentry,
    message: '認証トークンを再試行しても取得できず、ヘッダー無しで送信',
    level: 'warning',
    result: 'unavailable',
    error,
    path,
    method,
    url
  })

// 1 回目は失敗したが再試行で取れた(利用者には影響なし)ことを info で送る。発生頻度を把握するため
export const reportTokenRecovered = ({ sentry, error, path, method, url }) =>
  captureTokenEvent({
    sentry,
    message: '認証トークンの取得が 1 回失敗し、再試行で成功',
    level: 'info',
    result: 'recovered',
    error,
    path,
    method,
    url
  })

// 通知の間引き。kind(unavailable / recovered)ごとに、直近の通知から cooldownMs 以内なら false を返す
export const createReportThrottle = ({
  cooldownMs = TOKEN_REPORT_COOLDOWN_MS,
  now = () => Date.now()
} = {}) => {
  const lastReportedAt = {}
  return (kind) => {
    const current = now()
    const last = lastReportedAt[kind]
    if (last !== undefined && current - last < cooldownMs) {
      return false
    }
    lastReportedAt[kind] = current
    return true
  }
}
